import React, { useCallback, useEffect, useState } from 'react';
import { useEvent, useLazyCommand } from '@soap/modules';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import {
  externalProviderKinds,
  type ExternalProviderKind
} from '../CreateUnitForm/createUnitTypes';

type ProviderRow = {
  id: string;
  displayName: string;
  providerKind: string;
  notes?: string;
};

export function ExternalResourcesSettings() {
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loadProviders] = useLazyCommand('C328v1_GetExternalResourceProviders');
  const [addProvider] = useLazyCommand('C329v1_AddExternalResourceProvider');
  const [updateProvider] = useLazyCommand('C330v1_UpdateExternalResourceProvider');
  const [deleteProvider] = useLazyCommand('C331v1_DeleteExternalResourceProvider');

  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<ExternalProviderKind>(externalProviderKinds.bookPublisher);
  const [newNotes, setNewNotes] = useState('');

  const refresh = useCallback(() => {
    loadProviders({});
  }, [loadProviders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEvent({
    eventName: 'E328v1_GotExternalResourceProviders',
    onEventReceived: (event: Record<string, unknown>) => {
      const raw = (event.e328_Providers ?? event.E328_Providers) as Array<Record<string, unknown>> | undefined;
      setProviders(
        (raw ?? []).map(row => ({
          id: String(row.e328_ProviderId ?? row.E328_ProviderId ?? ''),
          displayName: String(row.e328_DisplayName ?? row.E328_DisplayName ?? ''),
          providerKind: String(row.e328_ProviderKind ?? row.E328_ProviderKind ?? ''),
          notes: String(row.e328_Notes ?? row.E328_Notes ?? '')
        }))
      );
    }
  });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    addProvider({
      c329_DisplayName: name,
      c329_ProviderKind: newKind,
      c329_Notes: newNotes.trim() || null
    });
    setNewName('');
    setNewNotes('');
  };

  const handleUpdate = (row: ProviderRow) => {
    updateProvider({
      c330_ProviderId: row.id,
      c330_DisplayName: row.displayName,
      c330_ProviderKind: row.providerKind,
      c330_Notes: row.notes || null
    });
  };

  const handleDelete = (id: string) => {
    deleteProvider({ c331_ProviderId: id });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Add provider</CardTitle>
          <CardDescription>
            Book publishers and online lesson providers appear in the Create Unit wizard when using external
            lessons.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Provider name"
          />
          <select
            value={newKind}
            onChange={e => setNewKind(e.target.value as ExternalProviderKind)}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value={externalProviderKinds.bookPublisher}>Book publisher</option>
            <option value={externalProviderKinds.onlineProvider}>Online provider</option>
          </select>
          <Input
            value={newNotes}
            onChange={e => setNewNotes(e.target.value)}
            placeholder="Notes (optional)"
          />
          <Button type="button" onClick={handleAdd} disabled={!newName.trim()}>
            <Plus className="w-4 h-4 mr-1" />
            Add provider
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your providers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {providers.length === 0 ? (
            <p className="text-sm text-gray-600">No providers configured yet.</p>
          ) : (
            providers.map(row => (
              <div key={row.id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <Input
                  value={row.displayName}
                  onChange={e =>
                    setProviders(prev =>
                      prev.map(p => (p.id === row.id ? { ...p, displayName: e.target.value } : p))
                    )
                  }
                />
                <select
                  value={row.providerKind}
                  onChange={e =>
                    setProviders(prev =>
                      prev.map(p =>
                        p.id === row.id ? { ...p, providerKind: e.target.value } : p
                      )
                    )
                  }
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value={externalProviderKinds.bookPublisher}>Book publisher</option>
                  <option value={externalProviderKinds.onlineProvider}>Online provider</option>
                </select>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => handleUpdate(row)}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(row.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
